import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { Button, ResponsiveButtonSizes } from '@/libraries/design/components/button/src/button';
import { ContentBlock } from '@/libraries/design/components/content-block/src/content-block';
import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { useDarkTheme } from '@/libraries/design/utils/dark-theme-provider';

import UniformTemplateModalButton from '../../template-modal-button/src/template-modal-button';

export interface UniformReturnToProductProps {
	header: string;
	subHeading?: string;
	buttonText: string;
	buttonUrl?: string;
	productKey?: string;
	templateName?: string;
	templateId?: string;
	linkText?: string;
	linkUrl?: string;
}

const UniformReturnToProduct = ({
	header,
	subHeading,
	buttonText,
	buttonUrl,
	productKey,
	templateName,
	templateId,
	linkText,
	linkUrl,
}: UniformReturnToProductProps) => {
	const { isDarkTheme } = useDarkTheme();

	if (!buttonUrl && !(productKey && templateName && templateId)) {
		return null;
	}

	const buttonSize: ResponsiveButtonSizes = {
		mobile: 'xlarge',
		tablet: 'xlarge',
		desktop: 'xlarge',
		xl_desktop: 'xlarge',
	};

	return (
		<div css={[containerStyles, isDarkTheme && darkThemeContainerStyles]}>
			<ContentBlock title={header} titleAs="h2" titleAppearsAs="card-title" text={subHeading} />
			<div css={ctaContainerStyles}>
				{linkText && linkUrl && <Link text={linkText} href={linkUrl} aria-label={linkText} />}
				{buttonUrl ? (
					<Button href={buttonUrl} responsiveSize={buttonSize}>
						{buttonText}
					</Button>
				) : (
					<UniformTemplateModalButton
						text={buttonText}
						productKey={productKey ?? ''}
						pluginModuleKey={templateId}
						templateName={templateName ?? ''}
						responsiveSize={buttonSize}
					/>
				)}
			</div>
		</div>
	);
};

const containerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300'),
	padding: token('space.300'),
	paddingBlockEnd: token('space.400'),
	borderRadius: wacToken('corner.radius.card'),
	// this is not design approved color for this component (as of 6/30/2026) just placeholder for light theme
	backgroundColor: wacToken('color.extended.blue100'),
});

const darkThemeContainerStyles = css({
	borderWidth: '1px',
	borderStyle: 'solid',
	borderColor: wacToken('color.neutral.light600'),
	backgroundColor: wacToken('color.neutral.light1000'),
});

const ctaContainerStyles = css({
	display: 'flex',
	justifyContent: 'space-between',
	width: '100%',
	alignItems: 'flex-end',
	gap: token('space.300'),
	flexWrap: 'wrap',
	[media.below.sm]: {
		flexDirection: 'column',
		gap: token('space.300'),
		alignItems: 'stretch',
	},
});

export default UniformReturnToProduct;
